export const REFUND_CONSTANTS = {
  // Authorization Messages
  UNAUTHORIZED_ACCESS: "You are not authorized to access this page",
  UNAUTHORIZED_DESIGNATION:
    "You are not authorized to access this page based on your designation",

  // Agent View Messages
  PENDING_REQUEST_WARNING:
    "A refund request is already pending. You cannot raise another request.",
  PACKAGE_SELECTION_REQUIRED: "Please select at least one package",
  REMARK_MANDATORY: "Remark is mandatory",
  DOCUMENT_UPLOAD_REQUIRED: "Minimum 1 document upload is required",
  GST_LOSS_CHECKBOX_REQUIRED:
    "Please confirm that you have uploaded relevant documents for GST loss case",
  CANNOT_SUBMIT_PENDING: "Cannot submit request while another is pending",

  // Manager View Messages
  MANAGER_REMARK_REQUIRED: "Please add a remark before taking action",
  GST_LOSS_WARNING:
    "This is a GST loss case. Please check all the documents carefully.",

  // Success Messages
  REFUND_CREATED_SUCCESS: (refundId) =>
    `Refund request created successfully. Refund ID: ${refundId}`,
  REFUND_WAS_APPROVED: "Refund request approved",
  REFUND_WAS_REJECTED: "Refund request rejected",

  // Error Messages
  FETCH_REFUND_DETAILS_ERROR: "Failed to fetch refund details",
  CREATE_REFUND_ERROR: "Failed to create refund request",
  APPROVE_REJECT_ERROR: (action) => `Failed to ${action} refund request`,

  // File Upload Messages
  FILE_UPLOAD_FAILED: "File upload failed",
  FILE_LIMIT_REACHED: "Maximum file limit reached",

  // GST Loss Case Validation Messages
  GST_LOSS_COMMON_FILE_REQUIRED:
    "At least 1 common file is required for GST loss case",
  GST_LOSS_FINANCE_HEAD_REQUIRED:
    "Finance head approval file is required for GST loss case",
  GST_LOSS_PRODUCT_HEAD_REQUIRED:
    "Product head approval file is required for GST loss case",
};

// Toast notification types
export const TOAST_TYPES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
};

// UI Text Constants
export const UI_TEXT = {
  // Page Titles
  AGENT_VIEW_TITLE: "New Refund Request",
  MANAGER_VIEW_TITLE: "Refund Request",

  // Section Headers
  PACKAGE_DETAILS: "Package Details",
  REFUND_DETAILS: "Refund Details",
  AGENT_REMARK: "Agent Remark",
  UPLOAD_ATTACHMENT: "Upload Attachments",

  // Button Text
  SEND_FOR_APPROVAL: "Send for Approval",
  APPROVE: "Approve",
  REJECT: "Reject",

  // Form Labels
  OPPORTUNITY_ID: "Opportunity Id",
  REFUND_ID: "Refund Id",
  REMARK: "Remarks",
  MANAGER_REMARK: "Remark (Common for all packages)*",
  SELECT_PACKAGE: "Select Package",
  PACKAGE_NAME: "Package Name",
  PACKAGE_ID: "Package ID",
  SALE_PRICE: "Sale Price",
  START_DATE: "Start Date",
  END_DATE: "End Date",
  DAY_CONSUMED: "Days Consumed",
  REVENUE_LOSS: "Revenue Loss",

  // GST Loss Related
  GST_LOSS_CASE_WARNING: "⚠️ GST LOSS CASE",
  GST_LOSS_DESCRIPTION:
    "This is a GST loss case. Please check all the documents carefully before proceeding.",
  GST_LOSS_CHECKBOX_TEXT:
    "This is a GST loss case, I have uploaded the relevant documents",

  // Pending Request Related
  PENDING_REQUEST_WARNING_TITLE: "⚠️ PENDING REQUEST",
  PENDING_REQUEST_WARNING_DESCRIPTION:
    "A refund request is already pending for this opportunity. Please wait for the current request to be processed before raising a new one.",

  // Package Status Messages
  PACKAGE_ALREADY_REFUNDED: "Package is already refunded",

  // Opportunity Type Validation
  NON_OWNER_OPPORTUNITY_ERROR:
    "You cannot raise a refund request for non-owner opportunity.",
  OWNER_OPPORTUNITY_REQUIRED_TITLE: "Owner Opportunity Required",

  // Calculations
  REFUNDED_AMOUNT: "Refunded Amount",
  REVENUE_LOSS_LABEL: "Revenue Loss",
  REVENUE_RETAINED: "Revenue Retained",

  // Placeholders
  REMARK_PLACEHOLDER: "Enter remarks (mandatory)",
  MANAGER_REMARK_PLACEHOLDER: "Enter your remark for approval/rejection...",
  PACKAGE_PREFIX: "Package",

  // Loading States
  LOADING_OPPORTUNITY_DETAILS: "Loading opportunity details...",
  LOADING_REFUND_DETAILS: "Loading refund details...",
  OPPORTUNITY_NOT_FOUND: "Opportunity details not found",
  REFUND_NOT_FOUND: "Refund request not found",
};

// File Upload Configuration
export const FILE_UPLOAD_CONFIG = {
  NON_GST_LOSS: {
    sections: [
      {
        key: "common",
        title: "Documents",
        maxFiles: 5,
        minRequired: 1,
        isMandatory: true,
        documentType: "Common_Documents",
      },
    ],
    totalMaxFiles: 5,
  },
  GST_LOSS: {
    sections: [
      {
        key: "common",
        title: "Documents",
        maxFiles: 3,
        minRequired: 1,
        isMandatory: true,
        documentType: "Common_Documents",
      },
      {
        key: "finance",
        title: "Finance Head Approval",
        maxFiles: 1,
        minRequired: 1,
        isMandatory: true,
        documentType: "Finance_Head_Approval",
      },
      {
        key: "product",
        title: "Product Head Approval",
        maxFiles: 1,
        minRequired: 1,
        isMandatory: true,
        documentType: "Product_Head_Approval",
      },
    ],
    totalMaxFiles: 5,
  },
  ALLOWED_EXTENSIONS: ".pdf,.jpeg,.jpg,.png,.mp3,.mp4,.mov",
};

// Document Types for Refund Requests
export const REFUND_DOCUMENT_TYPES = {
  COMMON: "Common_Documents",
  FINANCE_HEAD_APPROVAL: "Finance_Head_Approval",
  PRODUCT_HEAD_APPROVAL: "Product_Head_Approval",
};

// Approval Status Constants
export const APPROVAL_STATUS = {
  PENDING_APPROVAL: "PendingApproval",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

// Section Names for File Upload
export const UPLOAD_SECTIONS = {
  DOCUMENTS: "Documents",
  FINANCE_HEAD_APPROVAL: "Finance Head Approval",
  PRODUCT_HEAD_APPROVAL: "Product Head Approval",
};

// Requirement Messages
export const REQUIREMENT_MESSAGES = {
  MIN_ONE_FILE: "Minimum 1 file required",
  MIN_FILES: (count) => `Minimum ${count} file${count > 1 ? "s" : ""} required`,
};

// Currency Constants
export const CURRENCY_SYMBOL = "₹";

export const getFileUploadConfig = (isGstLossCase = false) => {
  return isGstLossCase
    ? FILE_UPLOAD_CONFIG.GST_LOSS
    : FILE_UPLOAD_CONFIG.NON_GST_LOSS;
};

export const getSectionConfig = (isGstLossCase = false, sectionKey = "common") => {
  const config = getFileUploadConfig(isGstLossCase);
  return config.sections.find((section) => section.key === sectionKey);
};

export const getTotalMaxFiles = (isGstLossCase = false) => {
  const config = getFileUploadConfig(isGstLossCase);
  return config.totalMaxFiles;
};

// Allowed Account IDs for Raise Refund Request
export const REFUND_ALLOWED_ACCOUNT_IDS = [];

// Allowed Agent Account Ids
export const REFUND_ALLOWED_AGENT_ACCOUNT_IDS = [];

export const checkRefundRaiseOnAccountId = (accountId) => {
  return REFUND_ALLOWED_ACCOUNT_IDS.length > 0
    ? REFUND_ALLOWED_ACCOUNT_IDS.includes(accountId)
    : true;
};

export const checkRefundRaiseOnAgentAccountId = (agentAccountId) => {
  return REFUND_ALLOWED_AGENT_ACCOUNT_IDS.length > 0
    ? REFUND_ALLOWED_AGENT_ACCOUNT_IDS.includes(agentAccountId)
    : true;
};

export const checkRefundRaiseOnAgentAndUserAcccountIds = ({
  customerAccountId,
  agentAccountId,
}) =>
  checkRefundRaiseOnAccountId(customerAccountId) &&
  checkRefundRaiseOnAgentAccountId(agentAccountId);
